import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { fetchEntityListAction } from "../../actions/entityListActions";
import { updateEntityAction } from "../../actions/updateEntityAction";

/**
 * Table column configuration for entity list
 */
export const entityListColumns = [
    {
        header: "Entity Name",
        accessor: "entityName",
        render: (row) => row.entity?.entityName || "-"
    },
    {
        header: "Contact Person",
        accessor: "contactPerson",
        render: (row) => row.entity?.contactPerson || "-"
    },
    {
        header: "Email",
        accessor: "emailAddress",
        render: (row) => row.entity?.emailAddress || "-"
    },
    {
        header: "Entity Category",
        accessor: "entityCategory",
        render: (row) => row.entity?.entityCategory || "-"
    },
    {
        header: "LOB Type",
        accessor: "lobType",
        render: (row) => row.entity?.lobType || "-"
    },
    {
        header: "Documents Status",
        accessor: "documentsStatus",
        render: (row) => {
            const documents = row.documents || [];
            const totalDocs = documents.length;
            const uploadedDocs = documents.filter(doc => doc.isUploaded).length;

            const status = totalDocs === 0 ? "No Documents" :
                uploadedDocs === totalDocs ? "Uploaded" :
                    uploadedDocs > 0 ? "Partial" : "Pending";

            return (
                <span className={`status-badge ${status.toLowerCase()}`}>
                    {status}
                </span>
            );
        }
    }
];

/**
 * Custom hook for managing entity list state and operations with server-side pagination
 */
export const useEntityList = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    const [entities, setEntities] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        pageSize: 10,
        hasNextPage: false,
        hasPreviousPage: false
    });
    const itemsPerPage = 10;

    useEffect(() => {
        loadEntityList(currentPage);
    }, [currentPage]);

    const loadEntityList = async (page = 1) => {
        setIsLoading(true);
        try {
            const response = await fetchEntityListAction(page, itemsPerPage);

            if (response.success) {
                const entities = response.data || [];
                const serverPagination = response.pagination || {};
                // console.log("Paginations : ", response);
                // Fix totalItems if server returns 0 but we have data
                const correctedPagination = {
                    ...serverPagination,
                    totalItems: serverPagination.totalItems || entities.length,
                    totalPages: serverPagination.totalPages || Math.ceil((serverPagination.totalItems || entities.length) / itemsPerPage),
                    hasNextPage: serverPagination.hasNextPage !== undefined ? serverPagination.hasNextPage : (page < (serverPagination.totalPages || 1)),
                    hasPreviousPage: serverPagination.hasPreviousPage !== undefined ? serverPagination.hasPreviousPage : (page > 1)
                };

                setEntities(entities);
                setPagination(correctedPagination);
            } else {
                toast.error(response.message || "Failed to load entity list");
                setEntities([]);
                setPagination({
                    currentPage: 1,
                    totalPages: 1,
                    totalItems: 0,
                    pageSize: itemsPerPage,
                    hasNextPage: false,
                    hasPreviousPage: false
                });
            }
        } catch (error) {
            toast.error("Failed to load entity list");
            setEntities([]);
            setPagination({
                currentPage: 1,
                totalPages: 1,
                totalItems: 0,
                pageSize: itemsPerPage,
                hasNextPage: false,
                hasPreviousPage: false
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handlePageChange = (page) => {
        if (page !== currentPage && page >= 1) {
            setCurrentPage(page);
        }
    };

    const handleEdit = (entityRow) => {
        setSelectedClient(entityRow);
        setIsEditModalOpen(true);
    };

    const handleSaveClient = async (updatedData) => {
        try {
            const response = await updateEntityAction(updatedData.id, updatedData);

            if (response.success) {
                toast.success("Entity updated successfully!");
                await loadEntityList(currentPage);
                return response;
            } else {
                toast.error(response.message || "Failed to update entity");
                throw new Error(response.message);
            }
        } catch (error) {
            toast.error("Failed to update entity");
            throw error;
        }
    };

    const handleCloseModal = () => {
        setIsEditModalOpen(false);
        setSelectedClient(null);
    };

    const handleDelete = (entityRow) => {
        const entityName = entityRow.entity?.entityName || "this entity";
        if (window.confirm(`Are you sure you want to delete ${entityName}?`)) {
            toast.info("Delete functionality coming soon");
        }
    };

    return {
        currentPage,
        setCurrentPage: handlePageChange,
        isEditModalOpen,
        selectedClient,
        entities,
        isLoading,
        itemsPerPage,
        pagination,
        handleEdit,
        handleSaveClient,
        handleCloseModal,
        handleDelete,
        loadEntityList,
        // For backward compatibility
        totalPages: pagination.totalPages,
        currentEntities: entities, // With server-side pagination, entities are already the current page
    };
};